/**
 * Alert Component
 * Feedback alert for messages and notifications
 * 
 * @file src/component-library/feedback/Alert.jsx
 */

import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Flex, Box } from '../layout/Layout';

/**
 * Alert Component
 * 
 * @example
 * <Alert status="success" title="Success!">
 *   Your changes have been saved.
 * </Alert>
 */
export const Alert = forwardRef(({
  children,
  status = 'info',
  variant = 'subtle',
  title,
  icon,
  isClosable = false,
  onClose,
  className,
  ...props
}, ref) => {
  const alertClasses = clsx(
    'alert',
    `alert--${status}`,
    `alert--${variant}`,
    className
  );

  const defaultIcons = {
    info: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
      </svg>
    ),
    success: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      </svg>
    ),
    warning: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
    ),
    error: (
      <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
      </svg>
    ),
  };

  return (
    <div ref={ref} role="alert" className={alertClasses} {...props}>
      <Flex align="start" gap={3}>
        <span className="alert__icon">
          {icon || defaultIcons[status]}
        </span>
        <Box className="alert__content">
          {title && <div className="alert__title">{title}</div>}
          {children && <div className="alert__description">{children}</div>}
        </Box>
        {isClosable && (
          <button
            type="button"
            className="alert__close"
            onClick={onClose}
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </Flex>
    </div>
  );
});

Alert.displayName = 'Alert';

Alert.propTypes = {
  children: PropTypes.node,
  status: PropTypes.oneOf(['info', 'success', 'warning', 'error']),
  variant: PropTypes.oneOf(['subtle', 'solid', 'left-accent', 'top-accent']),
  title: PropTypes.node,
  icon: PropTypes.node,
  isClosable: PropTypes.bool,
  onClose: PropTypes.func,
  className: PropTypes.string,
};

export default Alert;
